import { useEffect, useState } from 'react';
import type { HallMap, OpenOrderRow, ReservationRow } from '@dastarhan/contracts';
import { api } from '../api';

/** Выбор стола для заказа «в зале». Карта тянется при открытии —
 *  переименованный в офисе стол виден сразу, без перезапуска кассы. */
const HALL_CACHE = 'dastarhan.hall.cache';

export function TableDialog({ token, current, parked, meId, onPick, onPickRemote, onCancel }: {
  token: string;
  current: string | null;
  parked: Record<string, { items: number; ageMin: number }>;
  /** свои столы отличаются цветом от чужих — как на схеме зала */
  meId: string;
  onPick: (t: { id: string; name: string } | null,
    resv?: { id: string; deposit: number; guestName: string }) => void;
  onPickRemote: (row: OpenOrderRow) => void;
  onCancel: () => void;
}) {
  const [halls, setHalls] = useState<HallMap | null>(null);
  const [resv, setResv] = useState<ReservationRow[]>([]);
  const [remote, setRemote] = useState<OpenOrderRow[]>([]);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    /*
     * Карта зала обязана открываться и без интернета.
     *
     * Столы меняются раз в год, а связь пропадает раз в вечер —
     * держать зал заложником роутера бессмысленно. Живой ответ
     * освежает копию, офлайн поднимает последнюю известную
     * (Дым, 11.08: «карта недоступна без связи»)
     */
    api.hallMap(token)
      .then((m) => {
        setHalls(m);
        try { localStorage.setItem(HALL_CACHE, JSON.stringify(m)); } catch { /* тесно */ }
      })
      .catch(() => {
        const raw = localStorage.getItem(HALL_CACHE);
        if (raw) {
          try { setHalls(JSON.parse(raw) as HallMap); return; } catch { /* битый кэш */ }
        }
        setFailed(true);
      });
    // Метки броней: кассир видит «бронь 19:00» и не сажает случайных гостей
    // Столы, открытые другими кассами точки — официант видит кассира и наоборот
    api.openOrders(token)
      .then((r) => setRemote(r.rows))
      .catch(() => setRemote([]));
    api.reservationsToday(token)
      .then((r) => setResv(r.rows.filter((x) => x.status === 'PLANNED' || x.status === 'SEATED')))
      .catch(() => setResv([]));
  }, [token]);

  const badgeFor = (tableId: string): string | null => {
    const now = Date.now();
    const soon = resv
      .filter((r) => r.tableId === tableId)
      .filter((r) => new Date(r.startAt).getTime() + r.durationMin * 60_000 > now)
      .sort((x, y) => x.startAt.localeCompare(y.startAt))[0];
    if (!soon) return null;
    if (soon.status === 'SEATED') return 'занят: бронь';
    const time = new Date(soon.startAt).toLocaleTimeString('ru-RU', { hour: '2-digit', minute: '2-digit' });
    const minsTo = Math.round((new Date(soon.startAt).getTime() - now) / 60_000);
    // «скоро придут» — только в горизонте двух часов, дальше цифра шумит
    if (minsTo > 0 && minsTo <= 120) return `бронь ${time} · через ${minsTo} мин`;
    if (minsTo <= 0) return `бронь ${time} · уже ждём`;
    return `бронь ${time}`;
  };

  return (
    <div className="dialog-backdrop" role="dialog" aria-modal="true" aria-label="Выбор стола">
      <div className="dialog table-dialog">
        <h1>Какой стол?</h1>
        {!halls && !failed && <p className="hint">Загружаем карту зала…</p>}
        {failed && (
          <p className="hint">
            Карта зала ещё не сохранялась на этой кассе — подключитесь к
            интернету один раз, дальше столы будут доступны и без него.
            Пока можно вести заказ без стола.
          </p>
        )}
        {halls?.length === 0 && (
          <p className="hint">Столы ещё не расставлены — владелец добавит их в бэк-офисе, вкладка «Зал».</p>
        )}
        {/* Зал без столов не показываем: заголовок над пустотой
            выглядит поломкой, хотя это просто новый зал, куда
            владелец ещё не поставил столы */}
        {(halls ?? [])
          .filter((h) => h.tables.some((t) => !t.isVirtual))
          .map((h) => (
          <section key={h.id} className="hall-block">
            <span className="hall-name">{h.name}</span>
            <div className="tables">
              {/* Служебный стол «Ошибки» здесь не место: он для отложенных
                  позиций, а не для гостей. Попасть в него можно переносом */}
              {h.tables.filter((t) => !t.isVirtual).map((t) => (
                (() => {
                  const rem = !parked[t.id]
                    ? remote.find((r) => r.tableId === t.id)
                    : undefined;
                  return (
                <button key={t.id}
                  className={`btn table ${current === t.id ? 'on' : ''}`
                    // Те же цвета, что на схеме зала: свой занятый зелёный,
                    // ждёт оплаты красный, чужой серый. Иначе официант
                    // читает два экрана по разным правилам
                    + (parked[t.id] || rem?.openedById === meId ? ' t-mine' : '')
                    + (rem?.awaitingPay ? ' t-bill' : '')
                    + (rem && rem.openedById !== meId ? ' t-foreign' : '')}
                  onClick={() => {
                    if (rem) { onPickRemote(rem); return; }
                    const rv = resv.find((r) => r.tableId === t.id && r.status === 'PLANNED' && r.deposit > 0);
                    onPick({ id: t.id, name: t.name },
                      rv ? { id: rv.id, deposit: rv.deposit, guestName: rv.guestName } : undefined);
                  }}>
                  <b>{t.name}</b>
                  <span>{t.seats} мест</span>
                  {parked[t.id] && (
                    <span className={`resv-badge held ${parked[t.id]!.ageMin >= 60 ? 'stale' : ''}`}
                      title={parked[t.id]!.ageMin >= 60 ? 'Стол висит давно — гость ещё здесь?' : undefined}>
                      открыт: {parked[t.id]!.items} поз · {parked[t.id]!.ageMin < 60
                        ? `${parked[t.id]!.ageMin} мин`
                        : `${Math.floor(parked[t.id]!.ageMin / 60)} ч ${parked[t.id]!.ageMin % 60} м`}
                    </span>
                  )}
                  {rem && (
                    <span className="resv-badge remote">
                      {rem.openedByName} · {rem.items.length} поз
                    </span>
                  )}
                  {!parked[t.id] && !rem && badgeFor(t.id) && <span className="resv-badge">{badgeFor(t.id)}</span>}
                </button>
                  );
                })()
              ))}
            </div>
          </section>
        ))}
        <div className="mod-actions">
          {current && <button className="btn" onClick={() => onPick(null)}>Без стола</button>}
          <button className="btn ghost" onClick={onCancel}>Закрыть</button>
        </div>
      </div>
    </div>
  );
}
